/**
 * One deal-screening workflow: its recipe, and the Deal Room folders a new
 * deal can point at.
 *
 * The folder candidates are fetched on their own and best effort, like the
 * monitoring borrower candidates: a failure costs the Add-a-deal card its
 * list, never the page its render.
 *
 * `updateWorkflow` stores the SERVER's answer, not the caller's edit — the
 * recipe merge happens server-side (`merge_recipe_edits`, which also bumps the
 * version), so the settings tab always shows what is stored.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { dealScreeningService } from "../services/dealScreeningService";
import type {
	DealScreeningFolderCandidate,
	DealScreeningUpdateWorkflowPayload,
	DealScreeningWorkflow,
} from "../types/dealScreening";

export interface DealScreeningWorkflowData {
	workflow: DealScreeningWorkflow | null;
	folderCandidates: DealScreeningFolderCandidate[];
	/** The candidates request is still out; the picker says so. */
	foldersLoading: boolean;
	foldersError: boolean;
	loading: boolean;
	error: Error | undefined;
	refresh: () => void;
	reloadFolders: () => void;
	updateWorkflow: (
		patch: DealScreeningUpdateWorkflowPayload,
	) => Promise<DealScreeningWorkflow>;
}

export function useDealScreeningWorkflow(
	workflowId: string | undefined,
): DealScreeningWorkflowData {
	const [workflow, setWorkflow] = useState<DealScreeningWorkflow | null>(null);
	const [folderCandidates, setFolderCandidates] = useState<
		DealScreeningFolderCandidate[]
	>([]);
	const [foldersLoading, setFoldersLoading] = useState(true);
	const [foldersError, setFoldersError] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Separate counters: a folder refresh must not invalidate a recipe load.
	const requestId = useRef(0);
	const foldersRequestId = useRef(0);

	const load = useCallback(async () => {
		if (!workflowId) {
			setLoading(false);
			setError(new Error("No workflow was named in the URL."));
			return;
		}
		const id = ++requestId.current;
		setLoading(true);
		setError(undefined);
		try {
			const next = await dealScreeningService.getWorkflow(workflowId);
			if (!mounted.current || id !== requestId.current) return;
			setWorkflow(next);
		} catch (caught) {
			if (!mounted.current || id !== requestId.current) return;
			// Never leave another workflow's recipe under this one's heading.
			setWorkflow(null);
			setError(caught instanceof Error ? caught : new Error(String(caught)));
		} finally {
			if (mounted.current && id === requestId.current) setLoading(false);
		}
	}, [workflowId]);

	const loadFolders = useCallback(async () => {
		if (!workflowId) return;
		const id = ++foldersRequestId.current;
		setFoldersLoading(true);
		try {
			const next = await dealScreeningService.listFolderCandidates(workflowId);
			if (!mounted.current || id !== foldersRequestId.current) return;
			setFolderCandidates(next);
			setFoldersError(false);
		} catch {
			if (!mounted.current || id !== foldersRequestId.current) return;
			// A failed refresh keeps the last good list; only an empty one says so.
			setFoldersError(true);
		} finally {
			if (mounted.current && id === foldersRequestId.current) {
				setFoldersLoading(false);
			}
		}
	}, [workflowId]);

	useEffect(() => {
		void load();
	}, [load]);

	useEffect(() => {
		setFolderCandidates([]);
		setFoldersError(false);
		void loadFolders();
	}, [loadFolders]);

	const updateWorkflow = useCallback(
		async (patch: DealScreeningUpdateWorkflowPayload) => {
			if (!workflowId) throw new Error("No workflow was named in the URL.");
			const saved = await dealScreeningService.updateWorkflow(workflowId, patch);
			if (mounted.current) setWorkflow(saved);
			return saved;
		},
		[workflowId],
	);

	return useMemo(
		() => ({
			workflow,
			folderCandidates,
			foldersLoading,
			foldersError,
			loading,
			error,
			refresh: () => {
				void load();
			},
			reloadFolders: () => {
				void loadFolders();
			},
			updateWorkflow,
		}),
		[
			workflow,
			folderCandidates,
			foldersLoading,
			foldersError,
			loading,
			error,
			load,
			loadFolders,
			updateWorkflow,
		],
	);
}
